import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { KeyPreflight } from "@/components/KeyPreflight";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import Markdown from "@/components/Markdown";
import { LEVEL_META, STATUS_META } from "@/lib/constants";
import { Lock, Play, Square, Flag, Timer, Sparkles, CheckCircle2, Eye, EyeOff, Lightbulb, Download, ShieldCheck, Server } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

function useCountdown(expiresAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function LabDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [lab, setLab] = useState(null);
  const [err, setErr] = useState("");
  const [flag, setFlag] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showSolution, setShowSolution] = useState(false);
  const [starterBusy, setStarterBusy] = useState(false);

  // Fetch as a blob and VERIFY it is really a zip before saving it. The old
  // plain <a download> trusted whatever the path returned, so when the file was
  // absent the SPA fallback's index.html got saved as .zip and only failed much
  // later, in the learner's terminal, as "End-of-central-directory signature
  // not found". A bad response must fail here, visibly.
  const downloadStarter = async () => {
    setStarterBusy(true);
    try {
      const res = await api.get(`/labs/${slug}/starter-bundle`, { responseType: "blob" });
      const blob = res.data;
      const type = blob?.type || "";
      if (!type.includes("zip")) {
        const text = await blob.text().catch(() => "");
        let detail = "";
        try { detail = JSON.parse(text)?.detail || ""; } catch { /* not json */ }
        throw new Error(detail || `Server returned ${type || "an unknown type"} instead of a zip archive`);
      }
      if (blob.size === 0) throw new Error("The archive came back empty");

      const name = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1]
        || `${slug}-starter.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(e?.message || formatApiError(e?.response?.data?.detail));
    } finally {
      setStarterBusy(false);
    }
  };

  const countdown = useCountdown(lab?.active_instance?.expires_at);

  // When the running instance's countdown hits zero, refresh once so the
  // backend's reaped state (no active_instance) is reflected and the user
  // isn't left with a dead .ovpn to download.
  const justExpired = !!lab?.active_instance && countdown === "00:00:00";
  useEffect(() => {
    if (justExpired) load();
    // eslint-disable-next-line
  }, [justExpired]);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/labs/${slug}`);
      setLab(data);
    } catch (e) {
      // A retired lab (its slug removed from the seed) 404s here. Bookmarks and old
      // links shouldn't dead-end, so send the user back to the catalog instead.
      if (e.response?.status === 404) {
        toast.info("That lab is no longer available — it has been replaced or retired.");
        nav("/labs", { replace: true });
        return;
      }
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const launch = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/labs/${slug}/launch`);
      if (data?.lab_url) {
        toast.success("Lab is starting. Opening in a new tab.");
      } else {
        toast.success("Lab instance provisioned.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const downloadOvpn = () => {
    const cfg = lab?.active_instance?.vpn_config;
    if (!cfg) return;
    const blob = new Blob([cfg], { type: "application/x-openvpn-profile" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ilfal-${slug}.ovpn`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("VPN profile downloaded. Import it into OpenVPN Connect.");
  };

  const terminate = async () => {
    setBusy(true);
    try {
      await api.post(`/labs/${slug}/terminate`);
      toast("Lab terminated.");
      await load();
    } finally { setBusy(false); }
  };

  const skipLab = async () => {
    if (!window.confirm(
      `Skip "${lab?.title || 'this lab'}"?\n\n` +
      `This lab will be marked as INCOMPLETE in your progress. You can come back to it any time, but it ` +
      `will NOT count toward the level certificate until you actually capture the flag. Continue?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/labs/${slug}/skip`);
      toast(`Lab marked as incomplete. ${data.next_title ? `Moving on to: ${data.next_title}` : "No next lab found."}`);
      if (data.next_slug) {
        nav(`/labs/${data.next_slug}`);
      } else {
        nav(`/labs`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };



  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/labs/${slug}/submit-flag`, { flag });
      setResult(data);
      if (data.correct) {
        toast.success("Lab complete! 🎉");
        if (data.certificate) toast.success(`Certificate issued: ${data.certificate.verification_id}`);
        await load();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>
        <Link to="/labs"><Button variant="outline" className="mt-4">← Back to labs</Button></Link>
      </div>
    );
  }
  if (!lab) return <div className="max-w-4xl mx-auto px-6 py-12 text-slate-500">Loading lab…</div>;

  const lm = LEVEL_META[lab.level];
  const sm = STATUS_META[lab.status];
  const isNetwork = lab.lab_kind === "network";
  const expired = !!lab.active_instance && countdown === "00:00:00";
  const running = !!lab.active_instance && lab.active_instance.status === "running" && !expired;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/labs" className="text-sm text-slate-500 hover:text-slate-900" data-testid="lab-back-link">← All labs</Link>
      <div className="flex flex-wrap items-center gap-3 mt-3 mb-2">
        <Badge variant="outline" className={lm.chip}>{lm.label}</Badge>
        <Badge variant="outline" className={sm.chip}>{sm.label}</Badge>
        {lab.is_free && <Badge className="bg-blue-600"><Sparkles className="w-3 h-3 mr-1" /> Free</Badge>}
      </div>
      <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-slate-900 break-words" data-testid="lab-title">{lab.title}</h1>
      <p className="mt-2 text-slate-600 max-w-3xl">{lab.description}</p>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        {/* Markdown guide */}
        <div className="lg:col-span-2 min-w-0 order-last lg:order-first">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-1">Objective</div>
            <p className="text-sm text-amber-900 font-medium" data-testid="lab-objective">{lab.objective}</p>
          </div>
          {lab.starter_bundle && (
            <button
              type="button"
              onClick={downloadStarter}
              disabled={starterBusy}
              className="group w-full flex items-center gap-3 mb-6 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-slate-400 hover:shadow-sm transition-colors text-left disabled:opacity-60"
              data-testid="lab-starter-repo-download"
            >
              <Download className={`w-4 h-4 shrink-0 transition-colors ${starterBusy ? "text-slate-400 animate-pulse" : "text-slate-500 group-hover:text-slate-900"}`} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900">
                  {starterBusy ? "Preparing download…" : "Starter repository"}
                </span>
                <span className="block text-xs text-slate-500">Every Claude Code 101 practice project, to redo the exercises on your own machine</span>
              </span>
            </button>
          )}
          <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-8 overflow-hidden" data-testid="lab-guide">
            <Markdown>{lab.guide_markdown}</Markdown>
          </div>

          {/* Reveal solution */}
          {lab.solution_markdown && (
            <div className="mt-6 border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="lab-solution-block">
              <button
                onClick={() => setShowSolution((v) => !v)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                data-testid="lab-solution-toggle"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-display font-bold text-slate-900">
                      {showSolution ? "Hide solution" : "Reveal solution"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {showSolution ? "Try the lab yourself first for maximum learning." : "Step-by-step walkthrough with the working payload."}
                    </div>
                  </div>
                </div>
                {showSolution ? <EyeOff className="w-5 h-5 text-slate-400" /> : <Eye className="w-5 h-5 text-slate-400" />}
              </button>
              {showSolution && (
                <div className="border-t border-slate-200 px-5 sm:px-8 py-6 bg-amber-50/30 overflow-hidden" data-testid="lab-solution-content">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-4 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" /> Solution walkthrough
                  </div>
                  <Markdown>{lab.solution_markdown}</Markdown>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar: sticky controls */}
        <aside className="lg:col-span-1 min-w-0 order-first lg:order-last">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Lab Instance</div>
              {running ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-sm font-medium text-slate-900">Running</span>
                  </div>
                  <div className="font-mono text-3xl font-bold text-slate-900 mb-1" data-testid="lab-countdown">{countdown}</div>
                  <div className="text-xs text-slate-500 mb-4 flex items-center gap-1">
                    <Timer className="w-3 h-3" /> time remaining (2h max)
                  </div>
                  {lab.active_instance.lab_url && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-2">Lab URL</div>
                      <a
                        href={lab.active_instance.lab_url}
                        target="_blank"
                        rel="noreferrer"
                        className="block font-mono text-xs text-blue-700 hover:text-blue-900 underline break-all mb-3"
                        data-testid="lab-url-link"
                      >
                        {lab.active_instance.lab_url}
                      </a>
                      <Button
                        asChild
                        className="w-full"
                        data-testid="lab-open-button"
                      >
                        <a href={lab.active_instance.lab_url} target="_blank" rel="noreferrer">
                          Open lab →
                        </a>
                      </Button>
                      <div className="text-xs text-slate-600 mt-2">
                        Private URL. Only you can access it. Expires in 2 hours.
                      </div>
                    </div>
                  )}
                  {isNetwork && lab.active_instance.vpn_config && (
                    <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded" data-testid="lab-vpn-panel">
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-700 mb-2 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> VPN access
                      </div>
                      <Button onClick={downloadOvpn} className="w-full bg-indigo-600 hover:bg-indigo-500" data-testid="lab-download-vpn-button">
                        <Download className="w-4 h-4 mr-1" /> Download VPN profile (.ovpn)
                      </Button>
                      <ol className="text-xs text-slate-600 mt-3 space-y-1 list-decimal list-inside">
                        <li>Import the <code className="font-mono">.ovpn</code> into <strong>OpenVPN Connect</strong>.</li>
                        <li>Connect, then attack the targets below from your own terminal.</li>
                        <li>This profile reaches <strong>only your</strong> private range.</li>
                      </ol>
                      <div className="mt-3 pt-3 border-t border-indigo-100">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-700 mb-2 flex items-center gap-1">
                          <Server className="w-3 h-3" /> Targets
                        </div>
                        {(lab.active_instance.target_ips || []).length === 0 ? (
                          <div className="text-xs text-slate-500">Provisioning targets…</div>
                        ) : (
                          <div className="space-y-2" data-testid="lab-target-list">
                            {(lab.active_instance.target_ips || []).map((t) => (
                              <div key={t.name} className="text-xs">
                                <div className="font-mono font-semibold text-slate-900">{t.ip} <span className="text-slate-500 font-sans">· {t.name}</span></div>
                                {t.note && <div className="text-slate-500">{t.note}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-2">Other hosts exist deeper in the network — find them by pivoting.</div>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Button onClick={terminate} variant="outline" className="w-full text-rose-700 hover:text-rose-700" disabled={busy} data-testid="lab-terminate-button">
                      <Square className="w-4 h-4 mr-1" /> Terminate
                    </Button>
                    {lab.status !== "completed" && (
                      <Button
                        onClick={skipLab}
                        variant="ghost"
                        className="w-full text-slate-600 hover:text-slate-900"
                        disabled={busy}
                        data-testid="lab-skip-button"
                        title="Mark this lab as incomplete and move to the next lab"
                      >
                        Skip lab → next
                      </Button>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="text-xs text-slate-500 mb-1">{isNetwork ? "Range ID" : "Container"}</div>
                    <div className="font-mono text-xs text-slate-700 truncate" data-testid="lab-container-id">{lab.active_instance.container_id}</div>
                  </div>
                </>
              ) : expired ? (
                <div data-testid="lab-expired-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-sm font-medium text-slate-900">Session expired</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    This lab session has expired{isNetwork ? " and your VPN range was torn down" : ""}. Any
                    {isNetwork ? " .ovpn profile" : " URL"} you downloaded no longer works. Relaunch to get a
                    fresh {isNetwork ? "range and profile" : "environment"}.
                  </p>
                  <Button onClick={launch} className="w-full" disabled={busy} data-testid="lab-relaunch-button">
                    <Play className="w-4 h-4 mr-1" /> {busy ? "Provisioning…" : (isNetwork ? "Relaunch range" : "Relaunch lab")}
                  </Button>
                  {lab.status !== "completed" && (
                    <Button
                      onClick={skipLab}
                      variant="ghost"
                      className="w-full mt-2 text-slate-600 hover:text-slate-900"
                      disabled={busy}
                      data-testid="lab-skip-button"
                      title="Mark this lab as incomplete and move to the next lab"
                    >
                      Skip lab → next
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {lab.is_built === false ? (
                    <>
                      <p className="text-sm text-amber-700 mb-4">
                        <strong>Coming Soon.</strong> This lab's content is in production. We'll publish it as soon as it's ready. Check back shortly.
                      </p>
                      <Button className="w-full" disabled data-testid="lab-launch-button">
                        <Play className="w-4 h-4 mr-1" /> Coming Soon
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-slate-600 mb-4">{isNetwork ? "Spin up your private VPN range. You'll get a personal .ovpn profile and isolated targets. 2-hour limit." : "Launch a fresh isolated environment. 2-hour limit."}</p>
                      {lab.level === "claudecode101" && <KeyPreflight slug={slug} />}
                      <Button onClick={launch} className="w-full" disabled={busy} data-testid="lab-launch-button">
                        <Play className="w-4 h-4 mr-1" /> {busy ? "Provisioning…" : (isNetwork ? "Launch range" : "Launch lab")}
                      </Button>
                      {lab.status !== "completed" && (
                        <Button
                          onClick={skipLab}
                          variant="ghost"
                          className="w-full mt-2 text-slate-600 hover:text-slate-900"
                          disabled={busy}
                          data-testid="lab-skip-button"
                          title="Mark this lab as incomplete and move to the next lab"
                        >
                          Skip lab → next
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Flag submission */}
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-1">
                <Flag className="w-3 h-3" /> Submit Flag
              </div>
              {lab.status === "completed" ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-900">Already completed</span>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-2">
                  <Input
                    value={flag}
                    onChange={(e) => setFlag(e.target.value)}
                    placeholder="ILFAL{...}"
                    className="font-mono"
                    required
                    data-testid="lab-flag-input"
                  />
                  <Button type="submit" className="w-full" disabled={busy || !flag} data-testid="lab-flag-submit-button">
                    Submit flag
                  </Button>
                </form>
              )}
              {result && !result.correct && (
                <Alert variant="destructive" className="mt-3" data-testid="lab-flag-error"><AlertDescription>{result.message}</AlertDescription></Alert>
              )}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
